<!--
  Assegna Ordine — lista ordini da assegnare a un operatore.
  Redesigned 2026-06-16 (v2.10.0, Phase 1.5 Chunk I.1).

  Drops kendo-grid + 7 kendo-grid-column + 5 kendo-progressbar +
  2 kendo-datepicker + kendo-grid-command-column + 4 mat-spinner
  + ~30 inline-style blocks (40 kendo/ion refs → 0).

  Pattern Diakonix coerente con storico-ordini H.1 + warehouse-orders
  (display-grade):
    - Sticky header con titolo + chip viola "Parziali" + refresh
    - KPI banner 5-card (Totale / Da Assegnare / Parziali / Completi
      / Bloccati) con native SVG circular progress bars
    - Filter bar nativa (search + clear + date range)
    - Table-card Diakonix 7 colonne con padding 14×16
    - Row tinting viola se or_partial='S'
    - Per-row actions: ✏️ Assegna + 🔒/🔓 Blocca/Sblocca

  Sub-modale preserved:
    - app-assegna-ordine-dettagli (modale assegnazione, Chunk I.2)
-->

<div class="assegna-page">

  <!-- ─── Sticky header ────────────────────────────────────────── -->
  <header class="assegna-header">
    <div class="assegna-header__titles">
      <h1 class="assegna-header__title">
        ✏️ Assegna ordine
        @if (ordiniParziali.length > 0) {
          <span class="status-chip status-chip--partial"
            [title]="ordiniParziali.length + ' ordini parziali in lista'">
            {{ ordiniParziali.length }} parziali
          </span>
        }
      </h1>
      <p class="assegna-header__subtitle">
        @if (gridData.length > 0) {
          <strong>{{ gridData.length }}</strong>
          {{ gridData.length === 1 ? 'ordine da assegnare' : 'ordini da assegnare' }}
          @if (filterTerm) { · ricerca <strong>"{{ filterTerm }}"</strong> }
        } @else if (loading) {
          Caricamento ordini…
        } @else {
          Nessun ordine da assegnare al momento
        }
      </p>
    </div>

    <button
      type="button"
      class="refresh-btn"
      (click)="reloadFunction()"
      title="Sincronizza"
      aria-label="Sincronizza"
    >🔁</button>
  </header>

  <!-- ─── KPI Banner: 5 cards ──────────────────────────────────── -->
  <section class="kpi-grid" aria-label="Metriche aggregate">

    <!-- Totale Imponibile -->
    <article class="kpi-card kpi-card--total">
      <header class="kpi-card__head">
        <span class="kpi-card__icon" aria-hidden="true">📊</span>
        <h3 class="kpi-card__label">Totale Imponibile</h3>
      </header>
      <p class="kpi-card__value">
        {{ netValue | currency: 'EUR' : 'symbol' : '1.2-2' }}
      </p>
      <p class="kpi-card__sub">
        {{ gridData.length }} {{ gridData.length === 1 ? 'ordine' : 'ordini' }}
      </p>
      <div class="kpi-card__bar">
        <div class="kpi-card__bar-fill" [style.width.%]="100"></div>
      </div>
    </article>

    <!-- Completi -->
    <article class="kpi-card kpi-card--complete">
      <header class="kpi-card__head">
        <span class="kpi-card__icon" aria-hidden="true">✅</span>
        <h3 class="kpi-card__label">Ordini Completi</h3>
      </header>
      <p class="kpi-card__value">
        {{ etValueTotal | currency: 'EUR' : 'symbol' : '1.2-2' }}
      </p>
      <p class="kpi-card__sub">
        {{ ordiniCompleti }} {{ ordiniCompleti === 1 ? 'ordine' : 'ordini' }}
      </p>
      <div class="kpi-card__bar">
        <div class="kpi-card__bar-fill" [style.width.%]="completeOrdersPercentage"></div>
        <span class="kpi-card__bar-pct">{{ completeOrdersPercentage | number:'1.0-1' }}%</span>
      </div>
    </article>

    <!-- Parziali -->
    <article class="kpi-card kpi-card--partial">
      <header class="kpi-card__head">
        <span class="kpi-card__icon" aria-hidden="true">🟣</span>
        <h3 class="kpi-card__label">Ordini Parziali</h3>
      </header>
      <p class="kpi-card__value">
        {{ etValuePartial | currency: 'EUR' : 'symbol' : '1.2-2' }}
      </p>
      <p class="kpi-card__sub">
        {{ ordiniParziali.length }} {{ ordiniParziali.length === 1 ? 'ordine' : 'ordini' }}
      </p>
      <div class="kpi-card__bar">
        <div class="kpi-card__bar-fill" [style.width.%]="partialOrdersPercentage"></div>
        <span class="kpi-card__bar-pct">{{ partialOrdersPercentage | number:'1.0-1' }}%</span>
      </div>
    </article>
    
    <!-- Da Assegnare -->
    <article class="kpi-card kpi-card--pending">
      <header class="kpi-card__head">
        <span class="kpi-card__icon" aria-hidden="true">📥</span>
        <h3 class="kpi-card__label">Da Assegnare</h3>
      </header>
      <p class="kpi-card__value">
        {{ etValueDaAssegnare | currency: 'EUR' : 'symbol' : '1.2-2' }}
      </p>
      <p class="kpi-card__sub">
        {{ ordiniDaAssegnare }} {{ ordiniDaAssegnare === 1 ? 'ordine' : 'ordini' }}
      </p>
      <div class="kpi-card__bar">
        <div class="kpi-card__bar-fill" [style.width.%]="daAssegnarePercentage"></div>
        <span class="kpi-card__bar-pct">{{ daAssegnarePercentage | number:'1.0-1' }}%</span>
      </div>
    </article>

    <!-- Bloccati -->
    <article class="kpi-card kpi-card--blocked">
      <header class="kpi-card__head">
        <span class="kpi-card__icon" aria-hidden="true">🚫</span>
        <h3 class="kpi-card__label">Ordini Bloccati</h3>
      </header>
      <p class="kpi-card__value">
        {{ etValueBlocked | currency: 'EUR' : 'symbol' : '1.2-2' }}
      </p>
      <p class="kpi-card__sub">
        {{ blockedOrdersCount }} {{ blockedOrdersCount === 1 ? 'ordine' : 'ordini' }}
      </p>
      <div class="kpi-card__bar">
        <div class="kpi-card__bar-fill" [style.width.%]="blockedOrdersPercentage"></div>
        <span class="kpi-card__bar-pct">{{ blockedOrdersPercentage | number:'1.0-1' }}%</span>
      </div>
    </article>
  </section>

  <!-- ─── Filter bar ───────────────────────────────────────────── -->
  <section class="assegna-filters" aria-label="Filtri ricerca">

    <!-- Search -->
    <div class="filter-field">
      <label class="filter-field__label" for="searchAssegna">Cerca ordine</label>
      <div class="filter-field__input-wrap">
        <input
          id="searchAssegna"
          type="search"
          class="filter-field__input"
          placeholder="N° ordine o cliente…"
          [(ngModel)]="filterTerm"
          (keyup.enter)="searchOrders()"
        />
        @if (filterTerm) {
          <button
            type="button"
            class="filter-field__clear"
            (click)="clearSearch()"
            aria-label="Rimuovi filtro"
          >×</button>
        }
      </div>
    </div>

    <!-- Date range -->
    <div class="filter-field filter-field--dates">
      <label class="filter-field__label">Data ordine</label>
      <div class="filter-field__date-pair">
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.startingDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onStartDateChange($event)"
          aria-label="Data da"
        />
        <span class="filter-field__date-sep">→</span>
        <input
          type="date"
          class="filter-field__input filter-field__input--date"
          [ngModel]="filters.endDate | date:'yyyy-MM-dd'"
          (ngModelChange)="onEndDateChange($event)"
          aria-label="Data a"
        />
      </div>
    </div>

    <button
      type="button"
      class="btn btn--primary"
      (click)="searchOrders()"
      [disabled]="loading"
    >🔍 Cerca</button>
  </section>

  <!-- ─── Table card ───────────────────────────────────────────── -->
  <section class="assegna-table-card" aria-label="Elenco ordini">

    @if (loading) {
      <div class="table-skeleton" aria-busy="true">
        @for (i of [1, 2, 3, 4, 5, 6, 7, 8]; track i) {
          <div class="table-skeleton__row">
            <div class="table-skeleton__line table-skeleton__line--lg"></div>
            <div class="table-skeleton__line table-skeleton__line--md"></div>
            <div class="table-skeleton__line table-skeleton__line--sm"></div>
          </div>
        }
      </div>
    } @else if (filteredOrders.length === 0) {
      <div class="assegna-empty">
        <div class="assegna-empty__icon" aria-hidden="true">
          @if (filterTerm) { 🔎 } @else { 📭 }
        </div>
        <p class="assegna-empty__msg">
          @if (filterTerm) {
            Nessun ordine corrisponde a <strong>"{{ filterTerm }}"</strong>.
          } @else {
            Nessun ordine da assegnare al momento.
          }
        </p>
      </div>
    } @else {
      <div class="assegna-table-scroll">
        <table class="assegna-table" role="table">
          <thead>
            <tr>
              <th class="col-order is-sortable" (click)="sortBy('or_order_number')">
                N° ordine
                @if (sortColumn === 'or_order_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-customer is-sortable" (click)="sortBy('or_customer')">
                Cliente
                @if (sortColumn === 'or_customer') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-amount is-sortable" (click)="sortBy('or_net_value')">
                Totale
                @if (sortColumn === 'or_net_value') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-articles is-sortable" (click)="sortBy('or_articles_number')">
                N° Articoli
                @if (sortColumn === 'or_articles_number') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('or_order_date')">
                Data ordine
                @if (sortColumn === 'or_order_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-date is-sortable" (click)="sortBy('expected_shipping_date')">
                Consegna prevista
                @if (sortColumn === 'expected_shipping_date') {
                  <span class="sort-indicator">{{ sortDirection === 'asc' ? '▲' : '▼' }}</span>
                }
              </th>
              <th class="col-actions">Azioni</th>
            </tr>
          </thead>
          <tbody>
            @for (
              item of filteredOrders;
              track item.order_id + '-' + ($index)
            ) {
              <tr
                class="assegna-row"
                [class.assegna-row--partial]="isPartial(item)"
                [class.assegna-row--blocked]="isBlocked(item)"
                (click)="gotoDetails(item.or_gamma_id)"
                [title]="isPartial(item) ? 'Ordine parziale — clicca per assegnare' : 'Clicca per assegnare'"
              >
                <td class="col-order">
                  <span class="cell-order-num">{{ item.or_order_number || '—' }}</span>
                  @if (isPartial(item)) {
                    <span class="cell-partial-tag" aria-label="Ordine parziale">P</span>
                  }
                </td>
                <td class="col-customer">
                  <span class="cell-customer">{{ item.or_customer || '—' }}</span>
                </td>
                <td class="col-amount">
                  <span class="cell-amount">
                    {{ item.or_net_value | currency: 'EUR' : 'symbol' : '1.2-2' }}
                  </span>
                </td>
                <td class="col-articles">
                  <span class="cell-articles">{{ item.or_articles_number ?? 0 }}</span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.or_order_date ? (item.or_order_date | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-date">
                  <span class="cell-date">
                    {{ item.expected_shipping_date ? (item.expected_shipping_date | date:'dd/MM/yyyy') : '—' }}
                  </span>
                </td>
                <td class="col-actions" (click)="$event.stopPropagation()">
                  <div class="row-actions">
                    <button
                      type="button"
                      class="icon-btn icon-btn--primary"
                      title="Assegna ordine"
                      aria-label="Assegna ordine"
                      (click)="gotoDetails(item.or_gamma_id)"
                    >✏️</button>

                    @if (isBlocked(item)) {
                      <button
                        type="button"
                        class="icon-btn icon-btn--blocked"
                        title="Ordine bloccato — clicca per sbloccare"
                        aria-label="Sblocca ordine"
                        (click)="unblock(item)"
                      >🛑</button>
                    } @else {
                      <button
                        type="button"
                        class="icon-btn icon-btn--unblocked"
                        title="Ordine attivo — clicca per bloccare"
                        aria-label="Blocca ordine"
                        (click)="block(item)"
                      >🔓</button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </section>

</div>

<!-- ─── Sub-modale assegnazione (Chunk I.2 — restyle in arrivo) ── -->
@if (modalService.isAssegnaDet$ | async) {
  <app-assegna-ordine-dettagli
    [isAssegnaOn]="modalService.isAssegnaDet"
    [orderId]="selectedOrderId"
    (result)="onChildClose($event)"
  ></app-assegna-ordine-dettagli>
}
